import { HeroCardBody } from './HeroCardBody';
import { HeroCardPanel } from './HeroCardPanel';
import { HeroCardSearch } from './HeroCardSearch';
import { resolveHeroCardSearchCentered } from './resolveHeroCard';
import type { HeroCardRootProps, HeroCardSearchCenteredConfig } from './types';

export interface HeroCardSearchCenteredProps extends HeroCardRootProps {
  config: HeroCardSearchCenteredConfig;
  /** The Hero's own h1, rendered inside the panel — see `heading` below. */
  heading?: React.ReactNode;
}

/**
 * The Location Search Result page's card (10.02.11): the same body + PLZ/Ort
 * finder as the Prices card, centered and full-width instead of left-aligned
 * in the narrow slot. The two are one design in two schemas, so the Editor's
 * component choice carries the layout — the same split `hero-card-text` and
 * `hero-card-text-centered` already use.
 *
 * The card carries no title of its own. On this page the headline *is* the
 * page's h1, so the Hero passes its own down as `heading` and renders nothing
 * above the image; that keeps exactly one h1 on the page and keeps the
 * headline in the field Editors already use for it.
 */
export function HeroCardSearchCentered({
  config,
  heading,
  ...rest
}: HeroCardSearchCenteredProps) {
  const resolved = resolveHeroCardSearchCentered(config);

  return (
    // `solid`: the Hero paints the same cream behind this card, so panel and
    // page merge into one band below the image instead of reading as a box
    // floating on the photo.
    <HeroCardPanel align="center" surface="solid" {...rest}>
      {heading}
      {/* Narrower than the panel, so the lead-in wraps into a centred column
          rather than running the full card width. */}
      <div className="w-full max-w-2xl">
        <HeroCardBody nodes={resolved.body} />
      </div>
      <HeroCardSearch search={resolved.search} layout="inline" />
    </HeroCardPanel>
  );
}
